'use client';

import styled from '@emotion/styled';
import { useCallback, useEffect, useState } from 'react';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { AppInstallLink, AppOsType } from '@diritto/common/system/constants';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { ButtonType } from 'constants/button';
import { ModalParams, ModalType } from 'constants/modal';
import { useTrackingState } from 'contexts/TrackingProvider';
import { DirittoIcon } from 'icons/DirittoIcon';
import { Button } from 'ui-components/common/Button';
import { LinkButton } from 'ui-components/common/LinkButton';
import { ModalLayout } from 'ui-components/layout/ModalLayout';
import { SafeAreaBottomStyles } from 'ui-components/layout/styles';
import { useClient } from 'ui-hooks/useClient';
import { useShowModal } from 'ui-hooks/useShowModal';
import { ModalHandler } from 'utils/ModalHandler';

export const AppInstallBottomModal = () => {
  const client = useClient();
  const { tracker } = useTrackingState();
  const { isShow, revealModal, showModal, hideModal } = useShowModal();
  const [state, setState] = useState<ModalParams[ModalType.AppInstall]>();

  const appInstallUrl = client.isAosWeb ? AppInstallLink[AppOsType.Android] : AppInstallLink[AppOsType.Ios];

  const handleClickDownload = useCallback(() => {
    state?.onClickDownload?.();
    tracker.track(TrackingEventType.ClickAppInstallBottomModal);
    hideModal();
  }, [hideModal, state, tracker]);

  const handleClose = useCallback(() => {
    state?.onCancel?.();
    tracker.track(TrackingEventType.CloseAppInstallBottomModal);
    hideModal();
  }, [hideModal, state, tracker]);

  useEffect(() => {
    const listener = (show: boolean, params?: ModalParams[ModalType.AppInstall]) => {
      setState(params);

      if (show) {
        showModal();
      } else {
        hideModal();
      }
    };

    ModalHandler.addEventListener(ModalType.AppInstall, listener);

    return () => {
      ModalHandler.removeEventListener(ModalType.AppInstall);
    };
  }, [hideModal, showModal]);

  return (
    <ModalLayout isShow={isShow} revealModal={revealModal} isBottom>
      <StyledModal>
        <DirittoIcon className="w-[64px] h-[64px]" />
        <StyledTitle>{state?.title}</StyledTitle>
        <StyledDescription>{state?.description || ''}</StyledDescription>
        <StyledButtons>
          <LinkButton
            target="_blank"
            href={appInstallUrl}
            label="디리토 앱 다운로드하러 가기"
            onClick={handleClickDownload}
            fullWidth
          />
          <Button
            label={state?.cancelText || '닫기'}
            buttonType={ButtonType.SecondaryNoBorder}
            onClick={handleClose}
            fullWidth
          />
        </StyledButtons>
      </StyledModal>
    </ModalLayout>
  );
};

const StyledModal = styled.div`
  width: 100vw;
  max-height: 336px;
  background-color: ${Color.White};
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 32px;
  padding-right: 16px;
  padding-left: 16px;
  ${SafeAreaBottomStyles(16)}
`;

const StyledTitle = styled.p`
  margin-top: 24px;
  font-weight: ${FontWeight.Bold};
  ${Typography.Medium};
  color: ${Color.Black};
`;

const StyledDescription = styled.div`
  text-align: center;
  margin-top: 4px;
  ${Typography.XXSmall};
  color: ${Color.Gray600};
`;

const StyledButtons = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;
